import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useForm } from "react-hook-form"

import { type GoodsReceiptPalletLpnFormValues, goodsReceiptPalletLpnFormSchema } from "../schemas"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { FormTextField } from "@/components/UiKit"
import type { GoodsReceiptPallet } from "@/graphql/codegen/graphql"
import { useAppView } from "@/hooks/useAppView"
import { useUpdateGoodsReceiptPallet } from "@/screens/Tablet/hooks/useUpdateGoodsReceiptPallet"
import { classNames } from "@/utils/utils"

interface GoodsReceiptPalletLpnFormDialogProps {
  pallet: GoodsReceiptPallet
  onClose: () => void
  onSubmit: () => void
}

export const GoodsReceiptPalletLpnFormDialog: FC<GoodsReceiptPalletLpnFormDialogProps> = ({
  pallet,
  onClose,
  onSubmit,
}) => {
  const { isTabletView } = useAppView()
  const { updatePallet } = useUpdateGoodsReceiptPallet({
    onSuccess: onSubmit,
  })

  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty, isSubmitting },
  } = useForm<GoodsReceiptPalletLpnFormValues>({
    defaultValues: {
      lpn: pallet.lpn ?? "",
    },
    resolver: zodResolver(goodsReceiptPalletLpnFormSchema),
    mode: "onChange",
  })

  const canSubmit = isValid && isDirty && !isSubmitting

  const handleClose = () => {
    onClose()
  }

  const submitForm = async (data: GoodsReceiptPalletLpnFormValues) => {
    await updatePallet({
      goodsReceiptPalletId: pallet.goodsReceiptPalletId,
      destinationWarehouseShelvingId: undefined,
      lpn: data.lpn,
    })
  }

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth={!isTabletView && "sm"}
      fullWidth={!isTabletView}
      fullScreen={isTabletView}
      data-testid='lpn-form-dialog'
    >
      <DialogHeader title='Edit LPN' onClose={handleClose} />
      <form className='flex flex-1 flex-col' onSubmit={handleSubmit(submitForm)}>
        <DialogContent className='flex flex-col gap-6 p-10'>
          <FormTextField control={control} name='lpn' label='LPN' placeholder='Enter LPN' data-testid='lpn-field' />
        </DialogContent>

        <DialogActions className='flex justify-between gap-4 px-10 pb-6'>
          <Button
            appearance='outlined'
            variant='primary'
            className={classNames({ "flex-1": isTabletView })}
            onClick={handleClose}
            data-testid='cancel-button'
          >
            Cancel
          </Button>
          <Button
            type='submit'
            appearance='contained'
            variant='primary'
            className={classNames({ "flex-1": isTabletView })}
            disabled={!canSubmit}
            data-testid='submit-button'
          >
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
